import { useCallback, useEffect, useState } from "react";
import { useToken } from "../../server/auth/use-token";
import { Page } from "./types";
import { useDesignHuddleProjectClone } from "./use-design-huddle-project-clone";
import { copyImage, imageURL } from "./utils";

type ProjectState = {
  loading: boolean;
  error: Error | undefined;
  pages: Page[] | undefined;
  page?: number;
  id?: string | null;
  mustBeCloned: boolean;
};

export const useDesignHuddleProject = ({
  project_id,
  account_id,
  page_number,
  must_be_cloned = false,
}: {
  project_id?: string | null;
  account_id: string;
  page_number?: number;
  must_be_cloned?: boolean;
}): ProjectState & {
  setPage: (v: number) => void;
} => {
  const [state, setState] = useState<ProjectState>({
    loading: true,
    error: undefined,
    pages: undefined,
    page: page_number,
    id: project_id,
    mustBeCloned: Boolean(must_be_cloned),
  });

  const getToken = useToken();
  const cloneProject = useDesignHuddleProjectClone();

  useEffect(() => {
    if (!state.loading) return;
    if (!state.id) return;
    if (!account_id) return;

    if (state.mustBeCloned) {
      cloneProject(state.id)
        .then((newId) => {
          state.id &&
            void copyImage(getToken, imageURL(account_id, state.id), newId).catch(() => {
              // ignore this error
              return;
            });

          setState((prev) => ({ ...prev, id: newId, mustBeCloned: false }));
        })
        .catch((error: Error) => {
          setState((prev) => ({ ...prev, error, loading: false }));
        });
      return;
    }

    if (!state.mustBeCloned) {
      DSHDLib.getProject(state.id, (projectErr, project) => {
        if (projectErr) {
          setState((prev) => ({ ...prev, error: projectErr, loading: false }));
          return;
        }
        setState((prev) => ({ ...prev, pages: project.pages, loading: false, error: undefined }));
      });
    }
  }, [cloneProject, account_id, getToken, state]);

  const setPage = useCallback((page: number) => {
    setState((prev) => ({ ...prev, page }));
  }, []);

  return { ...state, setPage };
};
